// Modern i18next implementation for Nirvana
// This replaces the NIRV.L system with a more robust solution

let i18next, Backend;

// Load centralized language configuration - REQUIRED
// Note: language-config.js is bundled separately and creates window.LANGUAGE_CONFIG
function getLanguageConfig() {
  if (typeof require !== 'undefined') {
    return require('./language-config.js');
  } else if (typeof window !== 'undefined' && window.LANGUAGE_CONFIG) {
    return window.LANGUAGE_CONFIG;
  } else {
    throw new Error('CRITICAL: Language configuration not available. Ensure language-config.js is properly bundled.');
  }
}

const languageConfig = getLanguageConfig();
if (!languageConfig) {
  throw new Error('CRITICAL: Language configuration failed to load. Check language-config.js file.');
}

// Better environment detection
const isElectron = typeof window !== 'undefined' && window.process && window.process.type;
const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;

// Import differently based on environment
if (isElectron || (isNode && typeof require !== 'undefined')) {
  try {
    i18next = require('i18next');
    Backend = require('i18next-fs-backend');
  } catch (e) {
    console.warn('i18next modules not available, using fallback');
  }
}

class NirvanaI18next {
  constructor() {
    this.initialized = false;
    this.currentLanguage = languageConfig.defaultLanguage;
    this.translations = {};
  }

  // Load translations from Crowdin-compatible structure
  async loadTranslations() {
    const languages = languageConfig.getLanguageCodes();
    
    for (const lang of languages) {
      try {
        // Try to load from the file system first (only in Electron/Node environment)
        if ((isElectron || isNode) && typeof require !== 'undefined') {
          const fs = require('fs');
          const path = require('path');
          const filePath = path.join(__dirname, 'locales', lang, 'translation.json');
          
          if (fs.existsSync(filePath)) {
            const content = fs.readFileSync(filePath, 'utf8');
            this.translations[lang] = JSON.parse(content);
            console.log(`Loaded ${lang} translations from file (${Object.keys(this.translations[lang]).length} keys)`);
            continue;
          }
        }
        
        // Fallback: fetch from URL if in browser context (Crowdin structure)
        if (typeof fetch !== 'undefined') {
          // Try multiple possible paths for web deployment (ordered by likelihood)
          const possiblePaths = [
            `desktop/locales/${lang}/translation.json`,   // Astro deployment path (most likely)
            `/desktop/locales/${lang}/translation.json`,  // Absolute Astro path
            `locales/${lang}/translation.json`,           // Direct path (fallback)
            `/locales/${lang}/translation.json`           // Absolute path (fallback)
          ];
          
          for (const path of possiblePaths) {
            try {
              const response = await fetch(path);
              if (response.ok) {
                this.translations[lang] = await response.json();
                console.log(`Loaded ${lang} translations from URL ${path} (${Object.keys(this.translations[lang]).length} keys)`);
                break; // Exit the inner loop if successful
              }
            } catch (fetchError) {
              // Continue to next path if this one fails
              console.debug(`Failed to fetch from ${path}:`, fetchError.message);
            }
          }
          
          // If we got translations for this language, continue to next language
          if (this.translations[lang]) {
            continue;
          }
        }
        
        // If we can't load the translation file, log error and skip this language
        console.error(`Failed to load translations for ${lang} - no fallback available`);
        
      } catch (error) {
        console.error(`Failed to load translations for ${lang}:`, error);
      }
    }
    
    // Ensure we have at least English
    if (!this.translations['en']) {
      console.error('Critical: English translations not loaded!');
      return false;
    }
    
    return true;
  }

  // Remove the embedded fallback - no longer needed
  loadEmbeddedTranslations(lang) {
    console.warn(`loadEmbeddedTranslations called for ${lang} - this is deprecated`);
  }

  async init(language = null) {
    try {
      console.log('🚀 i18next initialization starting...');
      
      // Load all translation data
      const success = await this.loadTranslations();
      
      if (!success) {
        console.error('Failed to load translations');
        return false;
      }
      
      this.initialized = true;
      
      // i18next handles language detection using standard conventions
      let detectedLanguage = language; // explicit override if provided
      
      if (!detectedLanguage && typeof window !== 'undefined') {
        // Standard i18next detection order:
        // 1. localStorage i18nextLng (standard i18next storage)
        // 2. URL parameter ?lng=xx  
        // 3. Browser language
        
        const i18nextLng = window.localStorage?.getItem('i18nextLng');
        if (i18nextLng && this.translations[i18nextLng]) {
          detectedLanguage = i18nextLng;
          console.log('🎯 Language from localStorage i18nextLng:', detectedLanguage);
        }
        else if (window.location) {
          const urlParams = new URLSearchParams(window.location.search);
          const urlLng = urlParams.get('lng');
          if (urlLng && this.translations[urlLng]) {
            detectedLanguage = urlLng;
            console.log('🎯 Language from URL parameter:', detectedLanguage);
          }
        }
        else if (window.navigator) {
          const browserLang = window.navigator.language?.split('-')[0];
          if (browserLang && this.translations[browserLang]) {
            detectedLanguage = browserLang;
            console.log('🎯 Language from browser:', detectedLanguage);
          }
        }
      }
      
      // Fallback to English
      if (!detectedLanguage || !this.translations[detectedLanguage]) {
        detectedLanguage = 'en';
        console.log('🎯 Language fallback to English');
      }
      
      this.currentLanguage = detectedLanguage;
      
      console.log(`✅ i18next initialized with language: ${this.currentLanguage}`);
      
      return true;
    } catch (error) {
      console.error('Failed to initialize i18next:', error);
      return false;
    }
  }

  // Main translation function - replaces NIRV.L.key access
  t(key, options = {}) {
    if (!this.initialized) {
      console.warn('i18next not initialized, returning key:', key);
      return key;
    }
    
    const translations = this.translations[this.currentLanguage] || this.translations['en'];
    return translations[key] || key;
  }

  // Change language and update UI
  async changeLanguage(lng) {
    try {
      if (!this.translations[lng]) {
        console.warn(`Language ${lng} not available, staying with ${this.currentLanguage}`);
        return false;
      }
      
      console.log('🔄 i18next changing language to:', lng);
      this.currentLanguage = lng;
      
      // Save language preference to localStorage (standard i18next convention)
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('i18nextLng', lng);
        console.log('💾 Saved language preference to localStorage:', lng);
      }
      
      // Update NIRV.L proxy
      this.updateLegacyReferences();
      
      // Trigger UI update events
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('languageChanged', { 
          detail: { language: lng } 
        }));
        
        // Notify Electron main process to update menus
        if (window.electronLanguage && typeof window.electronLanguage.changeLanguage === 'function') {
          try {
            const result = await window.electronLanguage.changeLanguage(lng);
            if (result.success) {
              console.log('✅ Electron main process language updated to:', lng);
            } else {
              console.warn('⚠️ Failed to update main process language:', result.error);
            }
          } catch (error) {
            console.error('❌ Error updating main process language:', error);
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to change language:', error);
      return false;
    }
  }

  getCurrentLanguage() {
    return this.currentLanguage;
  }

  getAvailableLanguages() {
    return languageConfig.getLanguageCodes();
  }

  // Compatibility layer - creates NIRV.L-like object
  createLegacyObject() {
    if (!this.initialized) return {};
    
    // Create a proxy that intercepts property access
    return new Proxy({}, {
      get: (target, prop) => {
        if (typeof prop === 'string') {
          return this.t(prop);
        }
        return undefined;
      }
    });
  }

  // Update all NIRV.L references in the UI
  updateLegacyReferences() {
    if (!this.initialized) return;
    
    // Update NIRV.L to point to our proxy
    if (typeof window !== 'undefined' && window.NIRV) {
      window.NIRV.L = this.createLegacyObject();
    }
    
    // Don't call NIRV.refreshCurrentview() here - it causes infinite loops
    // The caller should handle UI refresh if needed
  }

  // Helper for pluralization
  plural(key, count, options = {}) {
    return this.t(key, { count, ...options });
  }

  // Helper for interpolation
  interpolate(key, data = {}) {
    let translation = this.t(key);
    
    // Replace {{variable}} syntax with data values
    if (typeof translation === 'string' && data) {
      Object.keys(data).forEach(variable => {
        const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
        translation = translation.replace(regex, data[variable]);
      });
    }
    
    return translation;
  }
}

// Global instance
const nirvanaI18next = new NirvanaI18next();

// Export for use in main process
if (typeof module !== 'undefined' && module.exports) {
  module.exports = nirvanaI18next;
}

// Make available in renderer process
if (typeof window !== 'undefined') {
  window.NirvanaI18next = nirvanaI18next;
  
  // Initialize immediately
  initializeI18next();
}

async function initializeI18next() {
  try {
    console.log('🚀 Starting i18next initialization...');
    
    const success = await nirvanaI18next.init();
    
    if (success) {
      console.log('✅ i18next initialized successfully');
      
      // Set up NIRV.L proxy immediately
      nirvanaI18next.updateLegacyReferences();
      
      // Notify Electron main process about the language
      if (typeof window !== 'undefined' && window.electronLanguage) {
        try {
          const currentLang = nirvanaI18next.getCurrentLanguage();
          await window.electronLanguage.initLanguage(currentLang);
          console.log('✅ Electron main process notified of language:', currentLang);
        } catch (error) {
          console.error('❌ Error notifying main process:', error);
        }
      }
      
    } else {
      console.error('❌ Failed to initialize i18next');
      createEmergencyFallback();
    }
  } catch (error) {
    console.error('❌ Error initializing i18next:', error);
    createEmergencyFallback();
  }
}

function createEmergencyFallback() {
  // Create emergency fallback NIRV.L
  if (typeof window !== 'undefined' && window.NIRV && !window.NIRV.L) {
    window.NIRV.L = new Proxy({}, {
      get: (target, prop) => {
        console.warn(`Translation not ready: ${prop}`);
        return prop;
      }
    });
  }
}

// Debug function to check language preference sources
function debugLanguagePreferences() {
  console.log('\n=== Language Preference Debug ===');
  
  // Check localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    console.log('localStorage.UILanguage:', window.localStorage.getItem('UILanguage'));
    console.log('localStorage.pref.UILanguage:', window.localStorage.getItem('pref.UILanguage'));
    
    const nirvPrefData = window.localStorage.getItem('pref.UILanguage');
    if (nirvPrefData) {
      try {
        const prefData = JSON.parse(nirvPrefData);
        console.log('Parsed pref.UILanguage:', prefData);
      } catch (e) {
        console.log('Failed to parse pref.UILanguage:', e);
      }
    }
    
    // List all localStorage keys that might be language-related
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('language') || key.includes('Language') || key.includes('lang') || key.startsWith('pref.'))) {
        console.log(`localStorage.${key}:`, localStorage.getItem(key));
      }
    }
  }
  
  // Check NIRV.prefs if available
  if (typeof window !== 'undefined' && window.NIRV && window.NIRV.prefs) {
    if (window.NIRV.prefs.UILanguage) {
      console.log('NIRV.prefs.UILanguage.value:', window.NIRV.prefs.UILanguage.value);
      console.log('NIRV.prefs.UILanguage._value:', window.NIRV.prefs.UILanguage._value);
    } else {
      console.log('NIRV.prefs.UILanguage: not available');
    }
  } else {
    console.log('NIRV.prefs: not available yet');
  }
  
  // Check current i18next state
  if (window.NirvanaI18next && window.NirvanaI18next.initialized) {
    console.log('Current i18next language:', window.NirvanaI18next.getCurrentLanguage());
    console.log('Sample translation (inbox):', window.NirvanaI18next.t('inbox'));
  }
  
  console.log('=== End Language Debug ===\n');
}

// Make debug function available globally
if (typeof window !== 'undefined') {
  window.debugLanguagePreferences = debugLanguagePreferences;
}
